// angular
import {
  afterNextRender,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnDestroy,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
// components
import { SortsBarComponent } from '@app/core/components/sorts-bar/sorts-bar.component';
import { ProfileCardsListComponent } from '@app/core/components/profile-cards-list/profile-cards-list.component';
import { SolutionsPeopleFiltersComponent } from '../solutions-people-filters/solutions-people-filters.component';
import { FiltersPanelComponent } from '@app/core/components/filters-panel/filters-panel.component';
import { SolutionsPeopleFiltersDialogComponent } from '../solutions-people-filters-dialog/solutions-people-filters-dialog.component';
// services
import { SolutionsService } from '@app/core/shared/services/solutions.service';
import { DialogService } from '@app/core/shared/services/dialog.service';
// types
import { PeopleFilterType } from '@app/core/shared/interfaces/solutions-profile.interface';
// constants
import { solutionsPeopleSortItems } from '@app/+solutions/shared/constants/solutions-people-sort-items';

@Component({
  selector: 'pwj-solutions-people-browse',
  standalone: true,
  imports: [
    SolutionsPeopleFiltersComponent,
    FiltersPanelComponent,
    SortsBarComponent,
    ProfileCardsListComponent,
    TranslateModule,
  ],
  templateUrl: './solutions-people-browse.component.html',
  styleUrl: './solutions-people-browse.component.scss',
})
export class SolutionsPeopleBrowseComponent implements OnDestroy {
  filterType = input<PeopleFilterType | null>(null);
  noContentMessageKey = input<string>('industries.noIndustries');

  readonly sortItems = solutionsPeopleSortItems;
  readonly filtersSheetOpen = signal(false);
  /** show filter button on sort bar when sidebar is hidden (< lg). */
  readonly showFiltersTriggerOnSortBar = signal(false);

  private readonly _solutionsService = inject(SolutionsService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _router = inject(Router);
  private readonly _document = inject(DOCUMENT);
  private readonly _platformId = inject(PLATFORM_ID);
  private readonly _dialogService = inject(DialogService);

  readonly profilesCount = computed(() => this._solutionsService.profilesCount());

  constructor() {
    afterNextRender(() => {
      if (!isPlatformBrowser(this._platformId)) {
        return;
      }
      const mq = window.matchMedia('(max-width: 65.99em)');
      const sync = () => this.showFiltersTriggerOnSortBar.set(mq.matches);
      sync();
      mq.addEventListener('change', sync);
      this._destroyRef.onDestroy(() => mq.removeEventListener('change', sync));
    });

    effect(() => {
      this.toggleMobileSheetState(this.filtersSheetOpen());
    });

    this._router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        this.filtersSheetOpen.set(false);
      });
  }

  onFiltersFromSortBar(): void {
    if (!isPlatformBrowser(this._platformId)) {
      return;
    }
    if (window.matchMedia('(min-width: 48em)').matches) {
      this.openSolutionsFiltersDialog();
    } else {
      this.openFiltersSheet();
    }
  }

  openFiltersSheet(): void {
    this.filtersSheetOpen.set(true);
  }

  closeFiltersSheet(): void {
    this.filtersSheetOpen.set(false);
  }

  openSolutionsFiltersDialog(): void {
    this._dialogService.open(SolutionsPeopleFiltersDialogComponent, {
      width: 'min(23.5rem, calc(100vw - 1.25rem))',
      maxWidth: 'calc(100vw - 1.25rem)',
      maxHeight: 'min(90vh, 44rem)',
      height: 'auto',
      panelClass: 'overlay-panel--filters-dialog',
    });
  }

  private toggleMobileSheetState(isOpen: boolean): void {
    if (!isPlatformBrowser(this._platformId)) return;

    const isMobileView = window.matchMedia('(max-width: 65.99em)').matches;
    const shouldEnable = isOpen && isMobileView;
    this._document.body.classList.toggle('solutions-mobile-sheet-open', shouldEnable);
    this._document.documentElement.classList.toggle(
      'solutions-mobile-sheet-open',
      shouldEnable
    );
  }

  ngOnDestroy(): void {
    if (!isPlatformBrowser(this._platformId)) return;
    this._document.body.classList.remove('solutions-mobile-sheet-open');
    this._document.documentElement.classList.remove('solutions-mobile-sheet-open');
  }
}
